import toast from 'react-hot-toast';

/**
 * Centralized toast notification utility
 * Provides consistent error and success message handling across the app
 */

// Track recently shown error messages to prevent duplicates
const recentErrorMessages = new Map();
const DEDUPLICATION_WINDOW = 3000; // 3 seconds

/**
 * Check if an error message was recently shown
 * @param {string} message - Error message to check
 * @returns {boolean} - True if message was shown recently
 */
const wasRecentlyShown = (message) => {
  const now = Date.now();
  const lastShown = recentErrorMessages.get(message);
  
  if (lastShown && (now - lastShown) < DEDUPLICATION_WINDOW) {
    return true;
  }
  
  recentErrorMessages.set(message, now);
  
  // Clean up old entries (older than deduplication window)
  for (const [msg, timestamp] of recentErrorMessages.entries()) {
    if (now - timestamp >= DEDUPLICATION_WINDOW) {
      recentErrorMessages.delete(msg);
    }
  }
  
  return false;
};

/**
 * Extract error message from RTK Query error object
 * @param {Object} error - RTK Query error object
 * @returns {string|React.ReactNode} - Error message or React element
 */
export const getErrorMessage = (error) => {
  // Handle PARSING_ERROR (usually proxy errors when backend is down)
  if (error?.status === 'PARSING_ERROR') {
    // Check if data is a string (proxy error message)
    if (typeof error.data === 'string') {
      // Extract a user-friendly message from proxy errors
      if (error.data.includes('ECONNREFUSED')) {
        return "We're having trouble reaching the server right now.";
      }
      if (error.data.includes('Proxy error')) {
        return "We're having trouble reaching the server right now.";
      }
      // Fallback: return the error data if it's a meaningful string
      return error.data;
    }
    // If data is not a string, try to extract message
    if (error?.data?.message) {
      return error.data.message;
    }
    return 'Unable to parse server response. Please try again.';
  }

  // Handle validation errors (multiple field errors)
  if (error?.data?.errors) {
    const errors = error.data.errors;
    const errorEntries = Object.entries(errors).filter(([_, message]) => Boolean(message));
    
    if (errorEntries.length === 0) {
      return 'Please correct the errors above';
    }
    
    // If only one error, return it directly
    if (errorEntries.length === 1) {
      const [_, message] = errorEntries[0];
      return message;
    }
    
    // If multiple errors, create a formatted list
    const errorList = errorEntries
      .map(([_, message]) => {
        return message;
      })
      .join('\n');
    
    return errorList;
  }

  // Handle single error message
  if (error?.data?.message) {
    return error.data.message;
  }

  // Handle network errors
  if (error?.status === 'FETCH_ERROR' || error?.error === 'FETCH_ERROR') {
    return 'Unable to connect to server. Please check your internet connection.';
  }

  // Handle RTK Query error format
  if (error?.error) {
    return error.error;
  }

  // Generic fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Format field name for better readability
 * @param {string} field - Field name (e.g., "bankDetails.accountNumber")
 * @returns {string} - Formatted field name (e.g., "Account Number")
 */
const formatFieldName = (field) => {
  // Handle nested fields (e.g., "bankDetails.accountNumber")
  if (field.includes('.')) {
    const parts = field.split('.');
    // Take the last part and format it
    const lastPart = parts[parts.length - 1];
    return formatFieldName(lastPart);
  }
  
  // Convert camelCase to Title Case
  // e.g., "accountNumber" -> "Account Number"
  return field
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
};

/**
 * Extract success message from server response
 * @param {Object} response - Server response object
 * @param {string} defaultMessage - Default success message if none provided
 * @returns {string} - Success message
 */
export const getSuccessMessage = (response, defaultMessage = 'Operation completed successfully') => {
  if (response?.message) {
    return response.message;
  }
  if (response?.data?.message) {
    return response.data.message;
  }
  return defaultMessage;
};

/**
 * Show error toast notification
 * @param {Object|string} error - Error object or error message string
 * @param {Object} options - Toast options
 */
export const showErrorToast = (error, options = {}) => {
  const message = typeof error === 'string' ? error : getErrorMessage(error);
  
  // Skip showing toast if the same message was shown recently
  if (wasRecentlyShown(message)) {
    return;
  }
  
  toast.error(message, {
    duration: options.duration || 4000,
    position: 'bottom-right',
    ...options,
  });
};

/**
 * Show success toast notification
 * @param {Object|string} response - Response object or success message string
 * @param {string} defaultMessage - Default message if response doesn't contain one
 * @param {Object} options - Toast options
 */
export const showSuccessToast = (response, defaultMessage = 'Operation completed successfully', options = {}) => {
  const message = typeof response === 'string' 
    ? response 
    : getSuccessMessage(response, defaultMessage);
  
  toast.success(message, {
    duration: options.duration || 3000,
    position: 'bottom-right',
    ...options,
  });
};

/**
 * Show info toast notification
 * @param {string} message - Info message
 * @param {Object} options - Toast options
 */
export const showInfoToast = (message, options = {}) => {
  toast(message, {
    duration: options.duration || 3000,
    position: 'bottom-right',
    icon: 'ℹ️',
    ...options,
  });
};

/**
 * Show warning toast notification
 * @param {string} message - Warning message
 * @param {Object} options - Toast options
 */
export const showWarningToast = (message, options = {}) => {
  toast(message, {
    duration: options.duration || 3000,
    position: 'bottom-right',
    icon: '⚠️',
    ...options,
  });
};

// Export default toast instance for direct access if needed
export default toast;

